export type LookupFieldType =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "select"
  | "select-remote";

export interface LookupField {
  key: string;
  label: string;
  type: LookupFieldType;
  required?: boolean;
  options?: { value: string; label: string }[]; // type: "select"
  remoteType?: string; // type: "select-remote" — another key in LOOKUP_CONFIG to source options from
  default?: unknown;
  // Dotted path to read this field's current value from when opening the edit
  // form, if it differs from `key` — needed where the list endpoint's
  // response shape exposes a nested relation object instead of the raw FK
  // (e.g. IncidentCategoryResource returns `service_level_agreement: {id,...}`,
  // not a bare `service_level_agreement_id`).
  readKey?: string;
}

export interface LookupColumn {
  key: string; // supports dotted paths for nested display, e.g. "department.name"
  label: string;
}

export interface LookupScopeFilter {
  // Query param the list endpoint scopes by server-side (AbstractLookupController::scopeColumn()).
  key: string;
  // Which column (by key) this filter's <select> sits under in the table's filter row.
  columnKey: string;
  // LOOKUP_CONFIG key to source the <select> options from.
  remoteType: string;
  // "All ..." placeholder label, e.g. "All Sectors".
  label: string;
}

export interface LookupConfig {
  key: string;
  label: string;
  description: string;
  listPath: string; // GET, relative to the v1 API root
  basePath: string; // POST base / PUT|DELETE base + "/{id}"
  fields: LookupField[];
  columns: LookupColumn[];
  // Parent-scoped lookups only — omit where the list endpoint has no scopeColumn().
  scopeFilter?: LookupScopeFilter;
}

export const LOOKUP_CONFIG: Record<string, LookupConfig> = {
  tags: {
    key: "tags",
    label: "Tags",
    description: "Tags used on policies and SOPs.",
    listPath: "lookups/tags",
    basePath: "lookups/tags",
    fields: [{ key: "name", label: "Name", type: "text", required: true }],
    columns: [{ key: "name", label: "Name" }],
  },
  "audit-types": {
    key: "audit-types",
    label: "Audit Types",
    description: "Types of audit (Operational, Compliance, ...).",
    listPath: "lookups/audit-types",
    basePath: "lookups/audit-types",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
  },
  "audit-methodologies": {
    key: "audit-methodologies",
    label: "Audit Methodologies",
    description: "Methodologies used when planning an audit.",
    listPath: "lookups/audit-methodologies",
    basePath: "lookups/audit-methodologies",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
  },
  "document-categories": {
    key: "document-categories",
    label: "Document Categories",
    description: "Categories available when uploading a document.",
    listPath: "lookups/document-categories",
    basePath: "lookups/document-categories",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [{ key: "name", label: "Name" }],
  },
  "document-types": {
    key: "document-types",
    label: "Document Types",
    description: "Types available when uploading a document.",
    listPath: "lookups/document-types",
    basePath: "lookups/document-types",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [{ key: "name", label: "Name" }],
  },
  "document-statuses": {
    key: "document-statuses",
    label: "Document Statuses",
    description: "Lifecycle statuses available for documents.",
    listPath: "lookups/document-statuses",
    basePath: "lookups/document-statuses",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [{ key: "name", label: "Name" }],
  },
  "document-modules": {
    key: "document-modules",
    label: "Document Modules",
    description: "Modules a document can be linked to.",
    listPath: "lookups/document-modules",
    basePath: "lookups/document-modules",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [{ key: "name", label: "Name" }],
  },
  "kpi-thresholds": {
    key: "kpi-thresholds",
    label: "KRI Configuration",
    description: "Define KRI source, formula, green/amber/red thresholds, frequency, owner and required action.",
    listPath: "lookups/kpi-thresholds",
    basePath: "lookups/kpi-thresholds",
    fields: [
      { key: "metric_key", label: "Metric Key", type: "text", required: true },
      { key: "label", label: "Label", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "source", label: "Source", type: "text" },
      { key: "formula", label: "Formula", type: "textarea" },
      {
        key: "comparison", label: "Comparison", type: "select", required: true,
        options: [
          { value: "gte", label: "Breach when value is at or above threshold (gte)" },
          { value: "lte", label: "Breach when value is at or below threshold (lte)" },
        ],
      },
      { key: "warning_value", label: "Warning Value", type: "number", required: true },
      { key: "breach_value", label: "Breach Value", type: "number", required: true },
      {
        key: "frequency", label: "Measurement Frequency", type: "select", required: true, default: "monthly",
        options: [
          { value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" },
          { value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" },
        ],
      },
      // A user picker (select-remote) isn't wired here — GET /users paginates
      // server-side with no "all" escape hatch (unlike the lookup endpoints),
      // so the generic select-remote fetch (per_page: 0) isn't safe against it.
      { key: "owner_id", label: "Owner (User ID)", type: "text", readKey: "owner.id" },
      { key: "required_action", label: "Required Action", type: "textarea" },
      { key: "department_id", label: "Department", type: "select-remote", remoteType: "departments" },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [
      { key: "label", label: "Label" },
      { key: "metric_key", label: "Metric Key" },
      { key: "frequency", label: "Frequency" },
      { key: "warning_value", label: "Warning" },
      { key: "breach_value", label: "Breach" },
    ],
  },
  "service-level-agreements": {
    key: "service-level-agreements",
    label: "Service Level Agreements",
    description: "SLA tiers used by incident categories.",
    listPath: "lookups/service-level-agreements",
    basePath: "lookups/service-level-agreements",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      {
        key: "unit", label: "Unit", type: "select", required: true,
        options: [
          { value: "minutes", label: "Minutes" },
          { value: "hours", label: "Hours" },
          { value: "days", label: "Days" },
        ],
      },
      { key: "duration", label: "Duration", type: "number", required: true },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "duration", label: "Duration" },
      { key: "unit", label: "Unit" },
    ],
  },
  departments: {
    key: "departments",
    label: "Departments",
    description: "Departments — the org-structure classification used across all modules.",
    listPath: "lookups/departments",
    basePath: "lookups/departments",
    fields: [{ key: "name", label: "Name", type: "text", required: true }],
    columns: [{ key: "name", label: "Name" }],
  },
  "report-types": {
    key: "report-types",
    label: "Report Types",
    description: "Report types available in the report builder.",
    listPath: "report-types/admin",
    basePath: "report-types",
    fields: [
      { key: "key", label: "Key", type: "text", required: true },
      { key: "label", label: "Label", type: "text", required: true },
      { key: "category", label: "Category", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "requires_date_range", label: "Requires Date Range", type: "boolean" },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [
      { key: "label", label: "Label" },
      { key: "category", label: "Category" },
      { key: "key", label: "Key" },
    ],
  },
  "capa-checklist-templates": {
    key: "capa-checklist-templates",
    label: "CAPA Checklist Templates",
    description: "Closure checklist items CAPA cases can require.",
    listPath: "capa-checklist-templates/admin",
    basePath: "capa-checklist-templates",
    fields: [
      { key: "label", label: "Label", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      {
        key: "requirement_type", label: "Requirement Type", type: "select", required: true,
        options: [
          { value: "attachment", label: "Attachment" },
          { value: "acknowledgement", label: "Acknowledgement" },
        ],
      },
      { key: "category", label: "Category", type: "text" },
      { key: "sort_order", label: "Sort Order", type: "number", default: 0 },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ],
    columns: [
      { key: "label", label: "Label" },
      { key: "requirement_type", label: "Requirement Type" },
      { key: "category", label: "Category" },
    ],
  },
  "incident-categories": {
    key: "incident-categories",
    label: "Incident Categories",
    description: "Categories used to classify an incident, each with an SLA.",
    listPath: "incident-categories",
    basePath: "incident-categories",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "service_level_agreement_id", label: "SLA", type: "select-remote", remoteType: "service-level-agreements", readKey: "service_level_agreement.id" },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "service_level_agreement.name", label: "SLA" },
    ],
  },
  "risk-categories": {
    key: "risk-categories",
    label: "Risk Categories",
    description: "Categories used to classify a risk register entry, each with an SLA.",
    listPath: "lookups/risk-categories",
    basePath: "lookups/risk-categories",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "service_level_agreement_id", label: "SLA", type: "select-remote", remoteType: "service-level-agreements", readKey: "service_level_agreement.id" },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "service_level_agreement.name", label: "SLA" },
    ],
  },
  "incident-checklist-items": {
    key: "incident-checklist-items",
    label: "Incident Closure Checklist Items",
    description: "Per-category checklist items required to close an incident.",
    listPath: "incident-checklist-items",
    basePath: "incident-checklist-items",
    fields: [
      { key: "incident_category_id", label: "Incident Category", type: "select-remote", remoteType: "incident-categories", required: true },
      { key: "item", label: "Checklist Item", type: "text", required: true },
    ],
    columns: [
      { key: "item", label: "Item" },
      { key: "category.name", label: "Category" },
    ],
    scopeFilter: { key: "incident_category_id", columnKey: "category.name", remoteType: "incident-categories", label: "All Categories" },
  },
};

export const LOOKUP_ORDER = [
  "departments",
  "tags", "audit-types", "audit-methodologies",
  "document-categories", "document-types", "document-statuses", "document-modules",
  "service-level-agreements", "incident-categories", "incident-checklist-items",
  "risk-categories",
  "kpi-thresholds", "capa-checklist-templates", "report-types",
];
